import { useEffect, useState } from "react";

import { Ear, Link2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
} from "~/components/ui/drawer";
import type { RequestableVoiceEntry } from "~/features/meditations/hooks/use-meditation-custom-voice";
import { useIsMobile } from "~/hooks/use-mobile";
import { trackEvent } from "~/lib/analytics";

// AIDEV-NOTE: Client-facing promo for practitioner voice, shown once before their
// practitioner has created one.
//
// Copy leads on comfort and continuity rather than novelty, and states the consent model
// up front ("<name> decides") — without that, "your therapist's voice" reads as something
// done TO the practitioner, which is the reaction most likely to make a client recoil.
// Deliberately avoids "AI voice" in the headline; it belongs in the fine print.
//
// Whatever the client picks, onResolved() runs and their meditation plays.
//
// PRESENTATION: bottom drawer on mobile, centred dialog on desktop. A centred dialog puts
// three stacked reason-lines and a two-line title into narrow centred text, which reads as
// an ad; left-aligned in a sheet it reads as an offer. Same content either way — only the
// container and alignment change.

const ANALYTICS_ID = "practitioner-voice-promo";

export function PractitionerVoicePromoDialog({
    open,
    target,
    onResolved,
}: {
    open: boolean;
    target: RequestableVoiceEntry | null;
    /** Always called on close — runs the play the client originally pressed. */
    onResolved: () => void;
}) {
    const [isSending, setIsSending] = useState(false);
    const isMobile = useIsMobile();
    const name = target?.shortLabel ?? "your practitioner";

    // AIDEV-NOTE: Tracked here rather than relying on Dialog's analyticsId. `open` is
    // controlled by the caller, and Radix only fires onOpenChange for interactions it
    // originates — so the wrapper's event never fired for this modal, and the drawer has
    // no equivalent at all. The Dialog still gets analyticsId (it is a required prop);
    // it cannot double-fire here because nothing ever opens this via a trigger.
    useEffect(() => {
        if (open) trackEvent("viewed modal", { modal: ANALYTICS_ID });
    }, [open]);

    async function handleAsk() {
        if (!target) {
            onResolved();
            return;
        }
        setIsSending(true);
        try {
            const response = await fetch("/api/practitioner-voice/request", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ practitionerId: target.practitionerId }),
            });
            const body = (await response.json().catch(() => ({}))) as {
                status?: string;
                requestedAt?: string | null;
            };

            if (!response.ok) {
                toast.error("Couldn't send that just now.");
            } else if (body.status === "already_pending") {
                toast(`You've already asked ${name} about this.`);
            } else if (body.status === "cooldown") {
                toast(`You asked ${name} about this recently.`);
            } else {
                toast.success(`We'll ask ${name}.`, {
                    description:
                        "Their voice will show up here if they set one up.",
                });
            }
        } catch {
            toast.error("Couldn't send that just now.");
        } finally {
            setIsSending(false);
            // Play regardless — a failed request must not cost them the meditation.
            onResolved();
        }
    }

    function handleOpenChange(next: boolean) {
        if (!next && !isSending) onResolved();
    }

    const title = `Hear your meditations in ${name}’s voice`;
    const description =
        "These meditations can be in your practitioner’s voice, if they choose to set one up.";

    const reasons = (
        <ul className="space-y-3 text-[13.5px] text-gray-700">
            <li className="flex gap-2.5">
                <Ear className="mt-0.5 size-4 shrink-0 text-brand-forest-600" />
                <span>
                    <strong>Familiar.</strong> The voice you already know, in
                    the moments between sessions.
                </span>
            </li>
            <li className="flex gap-2.5">
                <Link2 className="mt-0.5 size-4 shrink-0 text-brand-forest-600" />
                <span>
                    <strong>Continuity.</strong> Your work doesn&rsquo;t stop
                    when the session ends.
                </span>
            </li>
            <li className="flex gap-2.5">
                <ShieldCheck className="mt-0.5 size-4 shrink-0 text-brand-forest-600" />
                <span>
                    <strong>Their call.</strong> {name} decides. Nothing is
                    created without their consent, and they can remove it any
                    time.
                </span>
            </li>
        </ul>
    );

    const askButton = (
        <Button disabled={isSending} onClick={() => void handleAsk()}>
            {isSending && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
            Ask {name}
        </Button>
    );

    const notNowButton = (
        <Button variant="ghost" disabled={isSending} onClick={onResolved}>
            Not now
        </Button>
    );

    if (isMobile) {
        return (
            <Drawer open={open} onOpenChange={handleOpenChange}>
                {/* left-0 because DrawerContent only pins `right-0` — without it the
                    sheet sizes to its content instead of spanning the screen. */}
                <DrawerContent className="left-0">
                    <DrawerHeader className="text-left">
                        <DrawerTitle>{title}</DrawerTitle>
                        <DrawerDescription>{description}</DrawerDescription>
                    </DrawerHeader>

                    <div className="px-4">{reasons}</div>

                    <DrawerFooter>
                        {/* Ask first on mobile: it is the primary action and the one
                            within thumb reach at the bottom of the sheet. */}
                        {askButton}
                        {notNowButton}
                        <p className="text-[11px] text-muted-foreground">
                            We&rsquo;ll send one message. This is an AI
                            recreation of their voice, made only with their
                            consent.
                        </p>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog
            analyticsId={ANALYTICS_ID}
            open={open}
            onOpenChange={handleOpenChange}
        >
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {/* AIDEV-NOTE: Kept deliberately plain (QA feedback, Aug 2026). The
                        longer version named the product and "part check-ins"; to a client
                        mid-session that reads as marketing, and the modal is not where
                        anyone learns IFS vocabulary. */}
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>

                {reasons}

                <DialogFooter className="sm:justify-between">
                    {notNowButton}
                    {askButton}
                </DialogFooter>

                <p className="text-center text-[11px] text-muted-foreground">
                    We&rsquo;ll send one message. This is an AI recreation of
                    their voice, made only with their consent.
                </p>
            </DialogContent>
        </Dialog>
    );
}
